import React, { useState } from "react";
import Card from "components/card";
import { MdClose } from "react-icons/md";
import axios from "axios";
import { toast } from "react-hot-toast";

const EditOrganicRCN = ({ record, onClose, onUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState({
    id: record?.id || "",
    date: record?.date || "",
    truckNumber: record?.truckNumber || "",
    origin: record?.origin || "",
    weighingBridgeWeight: record?.weighingBridgeWeight || "",
    numberOfBags: record?.numberOfBags || "",
    entryMoisture: record?.entryMoisture || "",
    pricePerKg: record?.pricePerKg || "",
    calibrationMoisture: record?.calibrationMoisture || "",
    dustOffloaded: record?.dustOffloaded || "",
    dustAfterDrying: record?.dustAfterDrying || "",
    actualCalibratedBags: record?.actualCalibratedBags || "",
    actualCalibratedKg: record?.actualCalibratedKg || "",
    moistureToAutoclave: record?.moistureToAutoclave || ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      const response = await axios.put('https://usibras-web-backend.onrender.com/api/editorganicRCN', {
        id: formData.id,
        date: formData.date,
        truckNumber: formData.truckNumber,
        origin: formData.origin,
        weighingBridgeWeight: parseFloat(formData.weighingBridgeWeight),
        numberOfBags: parseInt(formData.numberOfBags),
        entryMoisture: parseFloat(formData.entryMoisture),
        pricePerKg: parseFloat(formData.pricePerKg),
        calibrationMoisture: parseFloat(formData.calibrationMoisture),
        dustOffloaded: parseFloat(formData.dustOffloaded),
        dustAfterDrying: parseFloat(formData.dustAfterDrying),
        actualCalibratedBags: parseInt(formData.actualCalibratedBags),
        actualCalibratedKg: parseFloat(formData.actualCalibratedKg),
        moistureToAutoclave: parseFloat(formData.moistureToAutoclave)
      });

      if (response.data.success) {
        toast.success(response.data.message);
        const updatedRecord = {
          id: response.data.data.custom_id,
          date: response.data.data.date,
          truckNumber: response.data.data.truckNumber,
          origin: response.data.data.origin,
          weighingBridgeWeight: parseFloat(response.data.data.weighingBridgeWeight),
          numberOfBags: parseInt(response.data.data.numberOfBags),
          entryMoisture: parseFloat(response.data.data.entryMoisture),
          pricePerKg: parseFloat(response.data.data.pricePerKg),
          calibrationMoisture: parseFloat(response.data.data.calibrationMoisture),
          dustOffloaded: parseFloat(response.data.data.dustOffloaded),
          dustAfterDrying: parseFloat(response.data.data.dustAfterDrying),
          actualCalibratedBags: parseInt(response.data.data.actualCalibratedBags),
          actualCalibratedKg: parseFloat(response.data.data.actualCalibratedKg),
          moistureToAutoclave: parseFloat(response.data.data.moistureToAutoclave),
          createdAt: response.data.data.created_at,
          updatedAt: response.data.data.updated_at
        };
        onUpdate(updatedRecord);
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to update record');
      }
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error(error.response?.data?.message || 'Failed to update record');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto backdrop-blur-sm bg-gray-900/80">
      <div className="relative w-full max-w-7xl p-4 h-[90vh]">
        <Card extra={"w-full h-full overflow-y-auto p-6"}>
          {/* Loading Overlay */}
          {isUpdating && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-navy-700/80">
              <div className="relative">
                <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
                <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
                <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
                <div className="mt-8 text-center">
                  <p className="text-lg font-medium text-gray-600 dark:text-white">Updating</p>
                  <p className="text-sm text-gray-400">Please wait...</p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-8 flex items-center justify-between">
            <div>
              <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                Edit Organic RCN Entry
              </h4>
              <p className="mt-2 text-base text-gray-600">
                ID: {formData.id}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isUpdating}
              className={`rounded-full bg-lightPrimary p-2 text-brand-500 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600 ${
                isUpdating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <MdClose className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Date*</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Truck Number*</label>
                <input
                  type="text"
                  name="truckNumber"
                  value={formData.truckNumber}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Origin*</label>
                <input
                  type="text"
                  name="origin"
                  value={formData.origin}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Weighing Bridge Weight (KG)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="weighingBridgeWeight"
                  value={formData.weighingBridgeWeight}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Number of Bags*</label>
                <input
                  type="number"
                  name="numberOfBags"
                  value={formData.numberOfBags}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Entry Moisture (%)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="entryMoisture"
                  value={formData.entryMoisture}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Price per KG (GHS)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="pricePerKg"
                  value={formData.pricePerKg}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Calibration Moisture (%)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="calibrationMoisture"
                  value={formData.calibrationMoisture}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Dust Offloaded (KG)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="dustOffloaded"
                  value={formData.dustOffloaded}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Dust After Drying (KG)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="dustAfterDrying"
                  value={formData.dustAfterDrying}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Actual Calibrated Bags*</label>
                <input
                  type="number"
                  name="actualCalibratedBags"
                  value={formData.actualCalibratedBags}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Actual Calibrated KG*</label>
                <input
                  type="number"
                  step="0.01"
                  name="actualCalibratedKg"
                  value={formData.actualCalibratedKg}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Moisture to Autoclave (%)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="moistureToAutoclave"
                  value={formData.moistureToAutoclave}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2 outline-none dark:bg-navy-800 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-navy-700 pt-4 pb-2 flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isUpdating}
                className={`rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900 ${
                  isUpdating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating}
                className={`rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900 ${
                  isUpdating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default EditOrganicRCN; 